import { Component, effect, OnInit } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    ActivationEnd, Event,
    NavigationEnd,
    NavigationStart,
    Router,
    RouterEvent,
} from '@angular/router';
import { filter, map, tap } from 'rxjs';
import { BreadcrumbsService } from './admin/services/breadcrumbs.service';
import { Store } from './shared/interfaces/store';
import { UsersService } from './shared/services/users.service';
import { BroadcastService } from './shared/broadcast.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    private currentUrl: string | undefined;

    private currentUrlParts: string[] = [];

    private rawBreadcrumbs: string[][][] = [];

    public constructor(
        private readonly router: Router,
        private readonly crumbs: BreadcrumbsService,
        private readonly users: UsersService,
        private readonly broadcast: BroadcastService,
    ) {
        effect(() => {
            this.handleStoreChanged(this.users.storeChange());
        });
    }

    public ngOnInit(): void {
        this.broadcast.bootstrap();

        this.router.events.pipe(
            filter((e: Event | RouterEvent) => e instanceof NavigationStart),
            tap(() => this.currentUrlParts = []),
        ).subscribe(() => this.rawBreadcrumbs = []);

        const activationEnd$ = this.router.events.pipe(
            filter((e: Event | RouterEvent): e is Event => e instanceof ActivationEnd),
            map((e: Event) => (e as unknown as ActivationEnd).snapshot as ActivatedRouteSnapshot),
        );

        activationEnd$.pipe(
            tap((s) => this.currentUrlParts.unshift(s.routeConfig?.path || '')),
        ).subscribe((e) => {
            const breadcrumbs = this.crumbs.process(e);
            this.rawBreadcrumbs.push(breadcrumbs);
        });

        activationEnd$.pipe(
            filter((d) => d.paramMap.has('store')),
            map((d) => parseInt(d.paramMap.get('store') || '0', 10)),
            filter((storeInRoute) => this.users.current()?.store.id !== storeInRoute),
        ).subscribe((storeInRoute) => {
            this.users.setActiveStore(storeInRoute, { emitEvent: false }).subscribe();
        });

        this.router.events.pipe(
            filter((e: Event | RouterEvent) => e instanceof NavigationEnd),
            tap(() => this.currentUrl = this.generateCurrentUrl()),
        ).subscribe(() => this.crumbs.breadcrumbs.next(
            this.rawBreadcrumbs.reduce((previous, current) => current.concat(previous), []),
        ));
    }

    private generateCurrentUrl(): string {
        const payload: string[] = [];
        for (const url of this.currentUrlParts) {
            const parts = url.split('/')
                .filter((p) => p === ':store' || p.indexOf(':') !== 0);

            payload.push(parts.join('/'));
        }

        return payload.join('/');
    }

    private handleStoreChanged(store: Store | undefined): void {
        if (!store || !this.currentUrl || this.currentUrl.indexOf('/:store/') === -1) {
            return;
        }

        const redirectTo = this.currentUrl.replace('/:store/', '/' + store.id + '/');
        this.router.navigate(['/' + redirectTo]);
    }
}
